import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import { ISenderIdentity, ISenderSignature } from '../../../../interfaces/models/campaign';
import { useCampaignsRequest } from '../../../../services/apis/campaigns/useCampaignsRequest';
import { useAppActions } from '../../../../stores/application/useAppActions';
import campaignSelectStyle from './campaignSelectStyle';
import LinkUrlDialog from './LinkUrlDialog';

/**
 * The sender's email signature editor — the Email signature tab in
 * Settings → Sending accounts.
 *
 * It belongs to the sender, not to a campaign: one signature is appended to every
 * email that person sends, in all of their campaigns. It used to be editable from
 * inside a campaign too, which implied a scope it never had.
 *
 * It is also editable before a mailbox exists. Gating it on a connected mailbox meant
 * the composer promised "your signature is appended automatically" while offering
 * nowhere to write one.
 *
 * The editor is contenteditable rather than a textarea because that is the only way
 * the logo survives: copying a signature out of Gmail or Outlook puts `text/html` on
 * the clipboard, and a textarea takes only `text/plain`. The trade-off is that we
 * accept arbitrary markup, which is why the server sanitizes it on write and rehosts
 * inline images.
 */

const EMPTY: ISenderSignature = { enabled: true, html: '' };

interface Props {
  identities: ISenderIdentity[];
  /**
   * The saved sender, so the page can fold it back into its own list — otherwise a
   * save is invisible to the surrounding list until it refetches.
   */
  onSaved?: (identity: ISenderIdentity) => void;
}

const IMAGE_MAX_BYTES = 2 * 1024 * 1024;
const IMAGE_TYPES = 'image/png,image/jpeg,image/gif,image/webp';

const CampaignEmailSignature: React.FC<Props> = ({ identities, onSaved }) => {
  const { setSenderSignature, uploadEmailImage } = useCampaignsRequest();
  const { setToast } = useAppActions();

  const [selectedId, setSelectedId] = useState<string>('');
  const [draft, setDraft] = useState<ISenderSignature>(EMPTY);
  const [mode, setMode] = useState<'paste' | 'html'>('paste');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  /** Bumped when the draft is replaced from outside the box, never on typing. */
  const [revision, setRevision] = useState(0);

  const boxRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const [imageBusy, setImageBusy] = useState(false);

  const selected = useMemo(
    () => identities.find(i => i._id === selectedId) ?? identities[0] ?? null,
    [identities, selectedId],
  );

  // Load the selected sender's stored signature into the editor. Keyed on the id, not
  // the object: the senders list is refetched on a timer, and reacting to a new object
  // identity would wipe whatever the user had typed since.
  useEffect(() => {
    if (!selected) return;
    setDraft({ enabled: selected.signature?.enabled !== false, html: selected.signature?.html || '' });
    setRevision(r => r + 1);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?._id]);

  /** Read by the callback ref, which fires outside render and would close over a stale draft. */
  const draftRef = useRef(draft);
  draftRef.current = draft;

  /*
    Seed the box the moment it mounts, not only when `revision` changes.

    The effect below runs as soon as the stored signature loads — but if the editor is
    not in the DOM at that instant, it writes nothing, and whatever mounts afterwards
    has no further change to react to. That is how a saved signature came back blank.
    Doing it from the ref means the box is filled whenever it appears, which holds no
    matter what wraps it.
  */
  const attachBox = useCallback((el: HTMLDivElement | null) => {
    boxRef.current = el;
    if (el && el.innerHTML !== draftRef.current.html) el.innerHTML = draftRef.current.html;
  }, []);

  // Write into the contenteditable only when the value came from somewhere other than
  // typing in it — assigning innerHTML on every render drops the caret to the start.
  useEffect(() => {
    const box = boxRef.current;
    if (!box || mode !== 'paste') return;
    if (box.innerHTML !== draft.html) box.innerHTML = draft.html;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, revision]);

  const readBox = () => {
    const box = boxRef.current;
    if (box) setDraft(prev => ({ ...prev, html: box.innerHTML }));
  };

  const clear = () => {
    if (boxRef.current) boxRef.current.innerHTML = '';
    setDraft(prev => ({ ...prev, html: '' }));
  };

  // ── Formatting ──────────────────────────────────────────────────────────────

  /** Remember where the caret was, so a toolbar click can put things back there. */
  const rememberCaret = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && boxRef.current?.contains(sel.anchorNode)) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreCaret = useCallback(() => {
    const el = boxRef.current;
    if (!el) return;
    el.focus();
    const range = savedRangeRef.current;
    if (!range) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, []);

  /**
   * execCommand is formally deprecated, but it remains the only thing every browser
   * implements for contenteditable formatting, and replacing it means shipping an
   * editor framework for a handful of buttons.
   */
  const exec = useCallback((command: string, arg?: string) => {
    restoreCaret();
    document.execCommand(command, false, arg);
    const box = boxRef.current;
    if (box) setDraft(prev => ({ ...prev, html: box.innerHTML }));
  }, [restoreCaret]);

  /** The Link button asks in our own dialog, not window.prompt — see LinkUrlDialog. */
  const [linkOpen, setLinkOpen] = useState(false);

  const addLink = useCallback(() => {
    // The dialog takes focus; this is where the link goes once it hands it back.
    rememberCaret();
    setLinkOpen(true);
  }, [rememberCaret]);

  // The address arrives already limited to http(s) — see normalizeLinkUrl.
  const applyLink = useCallback((url: string) => {
    setLinkOpen(false);
    exec('createLink', url);
  }, [exec]);

  const pickImage = useCallback(async (file: File) => {
    if (file.size > IMAGE_MAX_BYTES) { setError('That image is over 2MB.'); return; }
    setImageBusy(true);
    setError('');
    const res: any = await uploadEmailImage(file);
    setImageBusy(false);

    const url = res?.data?.url ?? res?.url;
    if (!url) { setError(res?.message || 'Could not upload that image.'); return; }
    exec('insertHTML', `<img src="${url}" style="max-width:100%;height:auto" />`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exec]);

  /** Reuse a signature already written for another sender. */
  const copyFrom = (id: string) => {
    const source = identities.find(i => i._id === id);
    if (!source) return;
    setDraft(prev => ({ ...prev, html: source.signature?.html || '' }));
    setRevision(r => r + 1);
  };

  const save = useCallback(async () => {
    if (!selected) return;
    setSaving(true);
    setError('');

    const res: any = await setSenderSignature(selected._id, draft);
    setSaving(false);

    const saved = res?.data?.identity;
    if (!saved) {
      setError(res?.message || 'Could not save the signature.');
      return;
    }

    /*
      Reported as a toast rather than a line under the field.

      The green text sat below the editor, near the hint and the error slot, and read as
      one more piece of small print rather than as the answer to the button that had
      just been pressed.

      What comes back is the sanitized, image-rehosted version, which can differ from
      what was pasted — so when an image moved or was dropped, the toast says that
      instead of a bare "saved". Letting a logo disappear quietly is the worse outcome.
    */
    const rehosted = res?.data?.imagesRehosted ?? 0;
    const dropped = res?.data?.imagesDropped ?? 0;
    const imageNote = [
      rehosted ? `${rehosted} image${rehosted > 1 ? 's' : ''} moved to our servers so mail clients can load ${rehosted > 1 ? 'them' : 'it'}` : '',
      dropped ? `${dropped} image${dropped > 1 ? 's' : ''} could not be used and ${dropped > 1 ? 'were' : 'was'} removed` : '',
    ].filter(Boolean).join(' · ');
    setToast({
      type: dropped ? 'warning' : 'success',
      message: imageNote ? `Signature saved — ${imageNote}.` : 'Signature saved.',
    });

    setDraft({ enabled: saved.signature?.enabled !== false, html: saved.signature?.html || '' });
    setRevision(r => r + 1);
    onSaved?.(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, draft, onSaved]);

  if (!identities.length) return null;

  const isEmpty = !stripToText(draft.html) && !/<img\b/i.test(draft.html);
  const others = identities.filter(i => i._id !== selected?._id && hasSignature(i));

  return (
    <Panel>
      <PlainField>
        <PlainLabel htmlFor="signature-sender">Sender</PlainLabel>
        <Select
          id="signature-sender"
          value={selected?._id ?? ''}
          onChange={e => setSelectedId(e.target.value)}
        >
          {identities.map(i => (
            <option key={i._id} value={i._id}>
              {i.name || i.linkedin?.name || i.linkedin?.email || 'Unnamed sender'}
              {i.mail?.address ? ` · ${i.mail.address}` : ''}
            </option>
          ))}
        </Select>
      </PlainField>

      <Controls>
        <Spacer />

        {others.length > 0 && (
          <Select
            value=""
            onChange={e => { if (e.target.value) copyFrom(e.target.value); }}
          >
            <option value="">Copy signature from another sender…</option>
            {others.map(i => (
              <option key={i._id} value={i._id}>
                {i.name || i.linkedin?.name || i.linkedin?.email || 'Unnamed sender'}
              </option>
            ))}
          </Select>
        )}
      </Controls>

      {mode === 'paste' ? (
        <EditorShell>
          <PasteBox
            ref={attachBox}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Email signature"
            $empty={isEmpty}
            data-placeholder="Add your email signature here…"
            onInput={readBox}
            onBlur={() => { rememberCaret(); readBox(); }}
            onKeyUp={rememberCaret}
            onMouseUp={rememberCaret}
          />
          {/* Under the box, not above it: the signature is usually pasted whole and
              then touched up, so the tools belong next to the result rather than in
              front of an empty field. */}
          <Toolbar onMouseDown={e => { e.preventDefault(); rememberCaret(); }}>
            <TBtn type="button" title="Bold" onClick={() => exec('bold')}><b>B</b></TBtn>
            <TBtn type="button" title="Italic" onClick={() => exec('italic')}><i>I</i></TBtn>
            <TBtn type="button" title="Underline" onClick={() => exec('underline')}><u>U</u></TBtn>
            <TSep />
            <TBtn type="button" title="Bulleted list" onClick={() => exec('insertUnorderedList')}><ListIcon /></TBtn>
            <TBtn type="button" title="Numbered list" onClick={() => exec('insertOrderedList')}><OrderedListIcon /></TBtn>
            <TSep />
            <TBtn type="button" title="Link" onClick={addLink}><LinkIcon /></TBtn>
            <TBtn type="button" title="Image" disabled={imageBusy} onClick={() => { rememberCaret(); fileRef.current?.click(); }}>
              <ImageIcon />
            </TBtn>
            <TSep />
            <TBtn type="button" title="Clear signature" onClick={clear}><TrashIcon /></TBtn>

            {/* Edit HTML lives in the toolbar, with the other things that act on the
                field. Below the box it sat among the explanatory small print, where it
                read as a footnote rather than as a control. */}
            <ToolbarSpacer />
            <LinkBtn type="button" onClick={() => setMode('html')}>Edit HTML</LinkBtn>
          </Toolbar>

          <input
            ref={fileRef}
            type="file"
            accept={IMAGE_TYPES}
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) pickImage(file);
            }}
          />
        </EditorShell>
      ) : (
        <HtmlShell>
          <HtmlArea
            rows={8}
            spellCheck={false}
            placeholder="<table>…</table>"
            value={draft.html}
            onChange={e => setDraft(prev => ({ ...prev, html: e.target.value }))}
          />
          <Toolbar>
            <ToolbarSpacer />
            <LinkBtn type="button" onClick={() => setMode('paste')}>Back to editor</LinkBtn>
          </Toolbar>
        </HtmlShell>
      )}

      {/*
        Everything explanatory sits below the field, in one place.

        The box used to be surrounded: a sentence above it about scope, a hint below
        about sanitizing, and the Edit HTML / Clear controls mixed in among them. Read
        together and read once, under the thing they describe, is easier to take in —
        and it leaves the editor itself uncluttered.
      */}
      <InfoBox>
        <InfoLine>
          <InfoIcon aria-hidden>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 11v5" /><path d="M12 7.6v.2" />
            </svg>
          </InfoIcon>
          {/*
            One line, as in the design — but not the design's sentence.

            That one reads "it is appended to every email that sender writes", which
            stopped being true when the signature became something you place: no
            {{signature}}, no signature. So this keeps the design's shape and says what
            the product actually does.

            The paragraph about stripped scripts and rehosted images is gone. It
            described something that happens correctly on its own, and nobody reads a
            third line of grey text.
          */}
          <span>
            A signature belongs to the sender, not to a campaign — the same one is available
            in all of their campaigns. Add <Token>{'{{signature}}'}</Token> from the{' '}
            <strong>Insert variable</strong> menu to place it in an email.
          </span>
        </InfoLine>
      </InfoBox>

      {/* No preview panel: the editor above is contenteditable, so it already renders
          the signature exactly as it will look. A second copy underneath showed the
          same thing twice. */}
      {!!error && <ErrText>{error}</ErrText>}

      <PlainFoot>
        <SaveBtn type="button" disabled={saving || !selected} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </SaveBtn>
      </PlainFoot>

      <LinkUrlDialog show={linkOpen} onHide={() => setLinkOpen(false)} onSubmit={applyLink} />
    </Panel>
  );
};

export default CampaignEmailSignature;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Text content of pasted markup, with the &nbsp; a browser leaves in an "empty"
 * contenteditable treated as nothing.
 */
function stripToText(html: string): string {
  return String(html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
}

function hasSignature(identity: ISenderIdentity): boolean {
  const html = identity?.signature?.html || '';
  return Boolean(stripToText(html)) || /<img\b/i.test(html);
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const svg = (d: React.ReactNode) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ display: 'block' }}>{d}</svg>
);

const ListIcon = () => svg(<><path d="M8 6h13M8 12h13M8 18h13" /><circle cx="3.5" cy="6" r="1" /><circle cx="3.5" cy="12" r="1" /><circle cx="3.5" cy="18" r="1" /></>);
const OrderedListIcon = () => svg(<><path d="M10 6h11M10 12h11M10 18h11" /><path d="M4 4h1v4M3 12h2.5L3 15.5h2.5M3 18h2.5v2H3v-2" /></>);
const LinkIcon = () => svg(<><path d="M10 13a5 5 0 007.5.5l3-3a5 5 0 00-7-7l-1.7 1.7" /><path d="M14 11a5 5 0 00-7.5-.5l-3 3a5 5 0 007 7l1.7-1.7" /></>);
const ImageIcon = () => svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></>);
const TrashIcon = () => svg(<><path d="M4 7h16" /><path d="M10 7V5h4v2" /><path d="M6 7l1 13h10l1-13" /></>);

// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = styles.colors.primary600;

/* Explicit width rather than relying on the parent to stretch it — the same implicit
   assumption made the paste box collapse to its content once before. */
const EditorShell = styled.div`
  width: 100%; box-sizing: border-box;
  border: 1px solid #cbd5e1; border-radius: 8px; background: #fff;
  overflow: hidden;
  &:focus-within { border-color: ${ACCENT}; }
`;

const Toolbar = styled.div`
  display: flex; align-items: center; gap: 2px; flex-wrap: wrap;
  padding: 6px 8px; border-top: 1px solid #eef1f6;
`;

const ToolbarSpacer = styled.div`flex: 1 1 auto;`;

const TBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border: none; background: none; cursor: pointer;
  border-radius: 6px; color: #1e293b;
  font-family: NunitoSans; font-size: 14px;
  &:hover:not(:disabled) { background: #eef1f6; color: ${ACCENT}; }
  &:disabled { opacity: 0.45; cursor: default; }
`;

const TSep = styled.div`
  width: 1px; height: 18px; background: #e2e8f0; margin: 0 4px;
`;

/* In 'plain' the Settings card already draws the border and padding — a second box
   inside it reads as a panel within a panel. */
/* No border or padding of its own — the Settings card it sits in already draws them,
   and a second box inside read as a panel within a panel. */
const Panel = styled.div`
  background: #fff;
  display: flex; flex-direction: column; gap: 10px;
`;

const PlainField = styled.div`
  display: flex; flex-direction: column; gap: 5px;
  max-width: 340px;
`;

const PlainLabel = styled.label`
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: #475569;
`;

const PlainFoot = styled.div`
  display: flex; align-items: center;
  padding-top: 2px;
`;

const Controls = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
`;

const Spacer = styled.div`flex: 1 1 auto;`;

const Select = styled.select`
  ${campaignSelectStyle}
  max-width: 320px;
`;



const PasteBox = styled.div<{ $empty?: boolean }>`
  width: 100%; box-sizing: border-box;
  min-height: 150px; max-height: 380px; overflow-y: auto;
  padding: 10px 12px; background: #fff;
  font-family: Arial, Helvetica, sans-serif; font-size: 13px; line-height: 1.5;
  color: #0f172a;
  &:focus { outline: none; border-color: ${ACCENT}; }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; }

  ${p => p.$empty && `
    &::before {
      content: attr(data-placeholder);
      color: #b6c0cd;
      font-family: NunitoSans; font-size: 12px;
      pointer-events: none;
    }
  `}
`;

/* The HTML view gets the same shell as the editor so "Back to editor" can sit in the
   same place "Edit HTML" did — the two swap without the control moving. */
const HtmlShell = styled(EditorShell)``;

const HtmlArea = styled.textarea`
  width: 100%; box-sizing: border-box; resize: vertical;
  border: none; border-radius: 8px 8px 0 0;
  padding: 8px 10px;
  font-family: Menlo, Consolas, monospace; font-size: 11px; line-height: 1.5;
  color: #0f172a; background: #fff;
  &:focus { outline: none; }
`;

const InfoBox = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  padding: 10px 12px; border-radius: 8px;
  background: #f8fafc; border: 1px solid #eef1f6;
`;

const InfoLine = styled.p<{ $muted?: boolean }>`
  /* Flex so the icon on the first line stays beside its sentence rather than being
     pushed onto its own row, and so the text wraps under itself, not under the icon. */
  display: flex; align-items: flex-start; gap: 6px;
  margin: 0;
  font-family: NunitoSans; font-size: 11px; line-height: 1.55;
  color: ${p => (p.$muted ? '#94a3b8' : '#64748b')};
  strong { font-weight: 800; color: #475569; }
`;

const InfoIcon = styled.span`
  flex: 0 0 auto; color: #94a3b8;
  /* Nudged down to sit on the first line's optical centre, not its top edge. */
  margin-top: 1px;
`;

const Token = styled.code`
  font-family: Menlo, Consolas, monospace; font-size: 10.5px;
  padding: 1px 4px; border-radius: 4px;
  background: ${styles.colors.primary100}; color: ${ACCENT};
`;

const SaveBtn = styled.button`
  flex: 0 0 auto;
  border: none; border-radius: 8px; padding: 8px 16px;
  background: ${ACCENT}; color: #fff; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  &:disabled { opacity: .6; cursor: default; }
`;

const LinkBtn = styled.button`
  border: none; background: none; padding: 0; cursor: pointer;
  color: ${ACCENT};
  font-family: NunitoSans; font-size: 11px; font-weight: 600;
`;

const ErrText = styled.span`
  font-family: NunitoSans; font-size: 11px; color: #dc2626;
`;
